import type { MaterialValue, PartDefinition } from "@rogue/rackbuilder"
import { BARS, TRAP_BAR } from "../../modelPaths"
import type { TrapBarOptions } from "../../RackTypes"

// Inches from the sleeve slot (the sleeve's inner end) out to the collar face where its 16" loadable length starts, measured off the model.
const COLLAR_OFFSET = { rackable: 10.38, nonRackable: 5.59 }
// The BARS hg 45 is 2.4" thick and centred on its own origin, so the first one sits half that past the collar.
const PLATE_THICKNESS = 2.4
const PLATES_PER_SIDE = 3

export class TrapBarBuilder {
  constructor(private config: TrapBarOptions) {}

  private createHandle(handle: string | undefined, material: MaterialValue | undefined): PartDefinition[] {
    if (!handle) {
      return []
    }

    return [{ mesh: `${TRAP_BAR}#handle ${handle}`, slots: {}, materials: material ? { handle: material } : undefined }]
  }

  private createBracket(left: boolean): PartDefinition {
    return {
      mesh        : `${TRAP_BAR}#bracket ${this.config.brackets}`,
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      slots       : {
        handle: this.createHandle(this.config.handle, this.config.materials.handle),
        // The engine logs an error for any requested slot the mesh lacks, and bracket single has no handle high.
        ...(this.config.brackets !== 'single' && {
          handle_high: this.createHandle(this.config.handleHigh, this.config.materials.handleHigh),
        }),
      },
    }
  }

  private createSleeve(left: boolean): PartDefinition {
    return {
      mesh        : `${TRAP_BAR}#sleeve ${this.config.rackable ? 'rackable' : 'non rackable'}`,
      adjustments : left ? [] : [{ type: 'rotate', axis: 'Y', amount: 180 }],
      materials   : {
        sleeve: [{ name: 'endcap', color: '#ffffff', config: { maps: { map: 'https://assets.roguefitness.com/image/upload/v1790800463/glb/txt/endcaps/tb3_iqz5le.jpg' } } }],
      },
      slots: {},
    }
  }

  // Shares the frame's sleeve slot rather than hanging off the sleeve, since the sleeve mesh has no slots of its own.
  private createGear(left: boolean): PartDefinition[] {
    if (!this.config.showEquipped) {
      return []
    }

    const collar = this.config.rackable ? COLLAR_OFFSET.rackable : COLLAR_OFFSET.nonRackable

    return Array.from({ length: PLATES_PER_SIDE }, (_, i) => {
      const distance = collar + PLATE_THICKNESS / 2 + i * PLATE_THICKNESS

      return {
        mesh        : `${BARS}#hg_45`,
        adjustments : [{ type: 'rotate', axis: 'Y', amount: 90 }],
        // The right slot is not rotated, so its sleeve runs the opposite way along Z.
        offset      : { z: left ? distance : -distance },
        slots       : {},
      }
    })
  }

  public build(): PartDefinition {
    return {
      mesh  : `${TRAP_BAR}#frame`,
      slots : {
        end           : { mesh: `${TRAP_BAR}#frame ${this.config.frameType}`, slots: {} },
        bracket_left  : this.createBracket(true),
        bracket_right : this.createBracket(false),
        sleeve_left   : [this.createSleeve(true), ...this.createGear(true)],
        sleeve_right  : [this.createSleeve(false), ...this.createGear(false)],
      },
    }
  }
}
